/**
 * Course Service
 * Handles all course-related API calls
 *
 * @module services/CourseService
 */

import BaseService from "core/api/BaseService";
import { API_ENDPOINTS } from "utils/constants";

class CourseService extends BaseService {
  constructor() {
    super(API_ENDPOINTS.COURSES.BASE);
  }

  /**
   * Get all courses with filters
   * @param {Object} params - Query parameters
   * @returns {Promise} List of courses
   */
  async getAll(params) {
    return this.get("", params);
  }

  /**
   * Get course by ID
   * @param {string} id - Course ID
   * @returns {Promise} Course details
   */
  async getById(id) {
    return this.get(`${id}`);
  }

  /**
   * Create new course
   * @param {Object} courseData - Course data
   * @returns {Promise} Created course
   */
  async create(courseData) {
    return this.post("", courseData);
  }

  /**
   * Update course
   * @param {string} id - Course ID
   * @param {Object} courseData - Updated data
   * @returns {Promise} Updated course
   */
  async update(id, courseData) {
    return this.put(`${id}`, courseData);
  }

  /**
   * Delete course
   * @param {string} id - Course ID
   * @returns {Promise} Deletion result
   */
  async delete(id) {
    return this.deleteCustom(`${id}`);
  }

  /**
   * Get course content (lessons)
   * @param {string} courseId - Course ID
   * @returns {Promise} Course content
   */
  async getContent(courseId) {
    return this.get(API_ENDPOINTS.COURSES.CONTENT(courseId));
  }

  /**
   * Get my courses as student
   * @returns {Promise} My enrolled courses
   */
  async getMyStudentCourses() {
    return this.get(API_ENDPOINTS.COURSES.MY_STUDENT_COURSES);
  }

  /**
   * Get my courses as teacher
   * @returns {Promise} My created courses
   */
  async getMyTeacherCourses() {
    return this.get("my-courses");
  }

  /**
   * Enroll in a course
   * @param {string} courseId - Course ID
   * @returns {Promise} Enrollment result
   */
  async enroll(courseId) {
    return this.post(`${courseId}/enroll`, {});
  }

  /**
   * Unenroll from a course
   * @param {string} courseId - Course ID
   * @returns {Promise} Unenrollment result
   */
  async unenroll(courseId) {
    return this.post(`${courseId}/unenroll`, {});
  }

  /**
   * Mark course as completed
   * @param {string} courseId - Course ID
   * @returns {Promise} Completion result
   */
  async markCompleted(courseId) {
    return this.post(`${courseId}/complete`, {});
  }

  /**
   * Get course statistics (for teachers)
   * @param {string} courseId - Course ID
   * @returns {Promise} Course statistics
   */
  async getStatistics(courseId) {
    return this.get(`${courseId}/statistics`);
  }

  /**
   * Get related courses
   * @param {string} courseId - Course ID
   * @returns {Promise} Related courses
   */
  async getRelatedCourses(courseId) {
    return this.get(`${courseId}/related`);
  }


  /**
   * Publish course (for teachers)
   * @param {string} courseId - Course ID
   * @returns {Promise} Publish result
   */
  async publish(courseId) {
    return this.post(`${courseId}/publish`, {});
  }

  /**
   * Unpublish course (for teachers)
   * @param {string} courseId - Course ID
   * @returns {Promise} Unpublish result
   */
  async unpublish(courseId) {
    return this.post(`${courseId}/unpublish`, {});
  }

  /**
   * Get course enrollments (for teachers)
   * @param {string} courseId - Course ID
   * @returns {Promise} Course enrollments
   */
  async getEnrollments(courseId) {
    return this.get(`${courseId}/enrollments`);
  }

  /**
   * Add lesson to course
   * @param {string} courseId - Course ID
   * @param {Object} lessonData - Lesson data
   * @returns {Promise} Created lesson
   */
  async addLesson(courseId, lessonData) {
    return this.post(`${courseId}/lessons`, lessonData);
  }

  /**
   * Update lesson
   * @param {string} courseId - Course ID
   * @param {string} lessonId - Lesson ID
   * @param {Object} lessonData - Updated lesson data
   * @returns {Promise} Updated lesson
   */
  async updateLesson(courseId, lessonId, lessonData) {
    return this.put(`${courseId}/lessons/${lessonId}`, lessonData);
  }

  /**
   * Delete lesson
   * @param {string} courseId - Course ID
   * @param {string} lessonId - Lesson ID
   * @returns {Promise} Delete result
   */
  async deleteLesson(courseId, lessonId) {
    return this.deleteCustom(`${courseId}/lessons/${lessonId}`);
  }

  /**
   * Mark lesson as completed
   * @param {string} courseId - Course ID
   * @param {string} lessonId - Lesson ID
   * @returns {Promise} Completion result
   */
  async markLessonCompleted(courseId, lessonId) {
    return this.post(`${courseId}/lessons/${lessonId}/complete`, {});
  }

  /**
   * Get course by slug (URL-friendly identifier)
   * @param {string} slug - Course slug
   * @returns {Promise} Course data
   */
  async getBySlug(slug) {
    return this.get(`slug/${slug}`);
  }
}

// Export singleton instance
export default new CourseService();

